import { Booking, ApiResponse, BookingChargeExportRow, type PastActiveTermCleanupPreview } from '@court-sync/shared';
import type { PaginationState } from '../components/ui/PaginatedTable';
import { useAuthStore } from '../store/auth';
import { apiGet, apiPost, apiPut, apiDelete, apiClient } from './apiClient';

export const bookingService = {
  async getBookings(filters?: {
    academyId?: string;
    customerId?: string;
    lessonId?: string;
    status?: string;
  }): Promise<ApiResponse<Booking[]>> {
    const params = filters || {};
    return apiGet<Booking[]>('/bookings', { params });
  },

  async getBooking(id: string): Promise<ApiResponse<Booking>> {
    return apiGet<Booking>(`/bookings/${id}`);
  },

  async createBooking(bookingData: {
    lessonId: string;
    termId: string;
    academyId: string;
    studentId: string;
  }): Promise<ApiResponse<{ id: string; totalAmount?: number }>> {
    return apiPost<{ id: string; totalAmount?: number }>('/bookings', bookingData);
  },

  async updateBooking(id: string, bookingData: Partial<Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>>): Promise<ApiResponse> {
    return apiPut(`/bookings/${id}`, bookingData);
  },

  async cancelBooking(id: string): Promise<ApiResponse> {
    return apiPost(`/bookings/${id}/cancel`);
  },

  async deleteBooking(id: string): Promise<ApiResponse> {
    return apiDelete(`/bookings/${id}`);
  },

  async removeEnrollment(
    bookingId: string,
    body: {
      academyId: string;
      scope: 'session' | 'term';
      sessionDate?: string;
      grantMakeupCredit?: boolean;
    }
  ): Promise<ApiResponse<{ scope: 'session' | 'term'; makeupCreditGranted: boolean; makeupCreditSkippedReason?: string }>> {
    return apiPost(`/bookings/${bookingId}/remove-enrollment`, body);
  },

  async sendBookingConfirmation(
    bookingId: string,
    academyId: string
  ): Promise<ApiResponse<{ sent: boolean }>> {
    return apiPost(`/bookings/${bookingId}/send-confirmation`, { academyId });
  },

  async getMyBookings(academyId?: string): Promise<ApiResponse<Booking[]>> {
    const platformUserId = useAuthStore.getState().user?.id ?? '';
    const params: Record<string, string> = { customerId: platformUserId };
    if (academyId) params.academyId = academyId;
    return apiGet<Booking[]>('/bookings', { params });
  },

  async getMyBookingsPaginated(
    academyId: string,
    page: number,
    limit: number,
    statusScope: 'active' | 'history' | 'all'
  ): Promise<{
    data: Array<{
      id: string;
      lessonId: string;
      lessonName: string;
      lessonType: string;
      studentName: string;
      coachNames: string;
      courtNames: string;
      termName: string;
      termDates: string;
      termStart: number;
      status: string;
      paymentStatus: string;
      canPayOnline: boolean;
      sessionNote: string;
      hasReceipt: boolean;
      paymentId: string | null;
      paymentAmount: number | null;
      paymentCurrency: string | null;
    }>;
    bookings: Booking[];
    pagination: PaginationState;
  }> {
    const platformUserId = useAuthStore.getState().user?.id ?? '';
    const response = await apiClient.get('/bookings', {
      params: {
        academyId,
        customerId: platformUserId,
        page,
        limit,
        statusScope,
      },
    });
    return response.data;
  },

  async getAcademyBookings(academyId: string): Promise<ApiResponse<Booking[]>> {
    return apiGet<Booking[]>('/bookings', {
      params: { academyId },
    });
  },

  async requestRefund(bookingId: string, reason?: string): Promise<ApiResponse> {
    return apiPost(`/bookings/${bookingId}/request-refund`, { reason });
  },

  async getAcademyBookingsPaginated(
    academyId: string,
    page: number,
    limit: number,
    filters?: { status?: string; lessonId?: string; termId?: string }
  ): Promise<{ data: Booking[]; pagination: PaginationState }> {
    const response = await apiClient.get<{ data: Booking[]; pagination: PaginationState }>('/bookings', {
      params: { academyId, page, limit, ...filters },
    });
    return response.data;
  },

  async exportAcademyBookingCharges(
    academyId: string,
    params: {
      from: string;
      to: string;
      dateBasis: 'booking_created' | 'payment_succeeded';
      status?: string;
      lessonId?: string;
      termId?: string;
      noTermOnly?: boolean;
      paymentStatus?: string;
    }
  ): Promise<ApiResponse<BookingChargeExportRow[]>> {
    return apiGet<BookingChargeExportRow[]>('/bookings/export/charges', {
      params: { academyId, ...params },
    });
  },

  async getPastActiveTermsCleanupPreview(
    academyId: string
  ): Promise<ApiResponse<PastActiveTermCleanupPreview>> {
    return apiGet<PastActiveTermCleanupPreview>(
      '/bookings/academy-past-active-terms-preview',
      { params: { academyId } }
    );
  },

  async completeEndedTermBookingsAndDeactivateTerm(body: {
    academyId: string;
    termIds: string[];
  }): Promise<ApiResponse<{ updatedBookings: number }>> {
    return apiPost<{ updatedBookings: number }>(
      '/bookings/academy-ended-term-cleanup',
      body
    );
  },
};
